/* eslint-disable max-lines */
// Hooks
import React, {useCallback, useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useLazyQuery} from "@apollo/client";
import {query as projectQuery, queryData, queryVars, useUpdateChildrenMutation, useUpdateProjectMutation
} from "./project-edit.graphql";
import {
  useAddChallengeMutation, useCopyChallengeMutation, useDeleteChallengeMutation,
   useMoveChallengeMutation, useReorderChallengeMutation
} from "./index.graphql";
import {useHistory, useParams} from "react-router";
import {useTour} from "../../providers/tour";
import {useCurrentUser} from "../../App.context";

// Libs, types and consts
import {reverse} from "../../routing";
import {getLanguageCode} from "../../i18n";
import {ProjectInput, Challenge, ChallengeInput} from "../../schema";
import {analyticsSendEvent, toEventData} from "../../libs/analytics";
import {getDataWithMock} from "../../tours";

// Components
import {useModal} from "../../components/ModalProvider";
import Layout, {Header, Main} from "../../components/Layout";
import Container from "../../ui/container";
import Button from "../../ui/button";
import ReactDocumentTitle from "../../components/ReactDocumentTitle";
import Breadcrumbs from "../../ui/breadcrumbs";
import {AddIcon} from "../../ui/icons";
import ChallengesTable, {ChallengeProps} from "../../components/editor/challenges-table/ChallengesTable";
import ReactLink from "../../components/ReactLink";
import ProjectCustomizeForm from "../../components/editor/ProjectCustomizeForm";
import EditableItemName from "../../components/editor/EditableItemName";
import Skeleton from "../../ui/skeleton";
import ShareButton from "../../components/editor/ShareButton";
import PageNotFoundScene from "../system/page-not-found";
import DeleteModal from "../../components/editor/DeleteModal";
import EditorProjectEditTour, {
  useMockData,
  classes as tourClasses,
  isEditorProjectEditTourMock,
  NAME as tourName
} from "./project-edit.tour";
import {DragDropContext, DropResult} from "react-beautiful-dnd";

// Classes
import cn from "classnames";
import classes from "./project-edit.module.css";

export default function EditorProjectEditScene() {
  const history = useHistory();

  const {t, i18n} = useTranslation();
  const language = getLanguageCode(i18n.language);

  const {runningTourName, bindToPage: bindTourToPage, run: runTour, mock: tourMock} = useTour();
  const tourMode = runningTourName === tourName;
  const isStaff = useCurrentUser()?.isStaff;

  useEffect(() => bindTourToPage(tourName), [bindTourToPage])

  const mock = useMemo(() => {
    if (isEditorProjectEditTourMock(tourMock)) {
      return tourMock;
    }
  }, [tourMock]);

  const mockData = useMockData(mock?.data.projectName);

  const {id: projectId} = useParams<{ id: string }>();

  useEffect(() => {
    analyticsSendEvent("editorProjectView", {projectId});
  }, [projectId]);

  const [query, {data, loading: fetching}] = useLazyQuery<queryData, queryVars>(projectQuery, {
    variables: {projectId, language}
  });

  useEffect(() => {
    if (projectId === "tour") {
      runTour(tourName);
    } else {
      query();
    }
  }, [projectId, runTour, query])

  const {project, projects} = useMemo(() => (
    getDataWithMock(data, mockData, tourMode)
  ), [data, mockData, tourMode]);

  const challenges = project?.challenges;
  const loading = !project;
  const shareProjectId = project?.id ? `${project?.id}_${project?.accessToken}` : projectId;

  const [updateProject] = useUpdateProjectMutation();
  const [addChallenge] = useAddChallengeMutation();
  const [deleteChallenge] = useDeleteChallengeMutation();
  const [moveChallenge] = useMoveChallengeMutation();
  const [copyChallenge] = useCopyChallengeMutation();
  const [reorderChallenge] = useReorderChallengeMutation();
  const [applyUpdateChildrenMutation] = useUpdateChildrenMutation();

  const shareLink = useMemo(() => (
    window.location.origin + reverse("playerProject", {id: shareProjectId})
  ), [shareProjectId])

  const onShareClick = useCallback(() => {
    analyticsSendEvent("editorCopyProjectShareLink", {projectId});
  }, [projectId]);

  const onProjectUpdate = useCallback((data: ProjectInput) => {
    analyticsSendEvent("editorProjectUpdate", {projectId});

    updateProject({
      variables: {id: projectId, data}
    })
  }, [projectId, updateProject]);

  const onAddChallenge = useCallback(() => {
    addChallenge({
      variables: {
        data: {
          name: t("editorIndex.newChallengeName"),
          languageCode: project?.languageCode,
          projectId,
        }
      }
    }).then(({data}) => {
      const challenge = data?.challenge!;

      analyticsSendEvent("editorAddChallenge", {
        challengeId: challenge.id
      });
      history.push(reverse("editorChallengeEdit", {
        id: challenge.id
      }))
    })
  }, [history, addChallenge, t, project?.languageCode, projectId]);

  const onChallengeCopy = useCallback((challengeId: Challenge["id"]) => {
    copyChallenge({
      variables: {id: challengeId}
    }).then(({data}) => {
      const challenge = data?.challenge!;

      analyticsSendEvent("editorCopyChallenge", {
        challengeId: challenge.id
      });
    })
  }, [copyChallenge]);

  const {add: addModal} = useModal();
  const onChallengeDelete = useCallback((challengeId: Challenge["id"]) => {
    const onDelete = () => {
      analyticsSendEvent("editorChallengeDelete", {
        challengeId: challengeId,
      });
      deleteChallenge({
        variables: {id: challengeId}
      });
      modal.remove();
    };

    const onCancel = () => modal.remove();

    const modal = addModal({
      id: `deleteChallenge_${challengeId}`,
      header: t("editorIndex.deleteChallenge"),
      content: t("editorIndex.deleteChallengeConfirmation"),
      footer: <DeleteModal.Footer onConfirm={onDelete} onCancel={onCancel}/>
    });
  }, [t, deleteChallenge, addModal]);

  const onChallengeMove = useCallback((challengeId: string, data: Pick<ChallengeInput, "projectId">) => {
    analyticsSendEvent("editorChallengeUpdate", {challengeId, ...toEventData(data)});

    moveChallenge({
      variables: {id: challengeId, data}
    })
  }, [moveChallenge]);

  const onChallengeReorder = useCallback((challengeId: string, data: Pick<ChallengeInput, "beforeChallengeId">) => {
    analyticsSendEvent("editorChallengeUpdate", {challengeId, ...toEventData(data)});

    reorderChallenge({
      variables: {id: challengeId, data}
    })
  }, [reorderChallenge]);

  const onDragEnd = useCallback((result: DropResult) => {
    if (!challenges) {
      return;
    }

    const {destination, source} = result;
    if (!destination) {
      return;
    }

    if (destination.index === source.index) {
      return;
    }

    const draggedChallenge = challenges[source.index];
    const beforeActionIndex = destination.index > source.index ? destination.index + 1 : destination.index;
    if (beforeActionIndex === challenges.length) {
      onChallengeReorder(draggedChallenge.id, {
        beforeChallengeId: null
      })
    } else {
      const targetAction = challenges[beforeActionIndex]
      onChallengeReorder(draggedChallenge.id, {
        beforeChallengeId: targetAction.id
      })
    }
  }, [challenges, onChallengeReorder])

  const onChildrenUpdate = useCallback(() => {
    analyticsSendEvent("editorProjectChildrenUpdate", {projectId: projectId});
    applyUpdateChildrenMutation({
      variables: {projectId}
    })
  }, [projectId, applyUpdateChildrenMutation]);

  if (!fetching && !project) {
    return <PageNotFoundScene/>
  }

  return (
    <>
      <ReactDocumentTitle title={project?.name ?? t("editorProjectEdit.title")}/>
      {tourMode && <EditorProjectEditTour returnToPreviousPage={projectId === "tour"}/>}
      <Layout>
        <Header/>
        <Main>
          <Container className={classes.main}>
            <Breadcrumbs className={classes.breadcrumbs}>
              <ReactLink
                className={tourClasses.editorMain}
                onClick={mock?.onReturnToMainPage}
                href={reverse("editor")}
              >
                {t("breadcrumbs.editor")}
              </ReactLink>
              <span title={project?.name} translate="no">
                {project?.name === undefined
                  ? t("breadcrumbs.challenge")
                  : project.name || <span className={classes.notSpecified}>{t("common.notSpecified")}</span>
                }
              </span>
            </Breadcrumbs>

            <div className={classes.header}>
              {!loading ? (
                <EditableItemName
                  key={project!.id}
                  className={tourClasses.projectName}
                  item={project!}
                  onSave={mock?.nameEditSave ?? onProjectUpdate}
                />
              ) : (
                <Skeleton.Text height={28} width={250}/>
              )}
              <div className={cn(classes.topRightMenu, tourClasses.topRightMenu)}>
                <Button as={ReactLink}
                  className={tourClasses.participants}
                  href={reverse("accountMembers") + "?projectId=" + encodeURIComponent(projectId)}
                >{t("editorProjectEdit.participants")}</Button>
                <ShareButton
                  className={tourClasses.shareButton}
                  value={shareLink}
                  name={project?.name}
                  onClick={onShareClick}
                  title={t("components.ShareButton.projectTitle")}
                  helpText={t("editorProjectEdit.shareButtonHelpText")}
                />
                {!loading && (
                  <>
                    <Button
                      className={cn(classes.addButton, tourClasses.addChallenge)}
                      onClick={onAddChallenge}
                      color="primary"
                    >
                      <AddIcon/>{t("editorIndex.addChallenge")}
                    </Button>
                    {isStaff && project?.isInitial && (
                      <Button
                        className={classes.previewButton}
                        onClick={onChildrenUpdate}
                        color='success'
                      >
                        {t("editorChallengeEdit.updateChildrenProjects")}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className={classes.content}>
              <div className={classes.contentMain}>
                <div className={classes.tableRoot}>
                  {(!loading && challenges && projects) ? (
                    <DragDropContext onDragEnd={onDragEnd}>
                      <ChallengesTable<ChallengeProps>
                        className={tourClasses.challengeTable}
                        allowDragNDrop
                        data={challenges}
                        currentProject={project}
                        projects={projects}
                        onChallengeDelete={onChallengeDelete}
                        onChallengeMove={onChallengeMove}
                        onChallengeCopy={onChallengeCopy}
                      />
                    </DragDropContext>
                  ) : (
                    <ChallengesTable.Skeleton/>
                  )}
                </div>
              </div>
              <div className={classes.contentAside}>
                {project && (
                  <ProjectCustomizeForm
                    className={tourClasses.projectCustomizeForm}
                    project={project}
                    onSave={onProjectUpdate}
                  />
                )}
              </div>
            </div>
          </Container>
        </Main>
      </Layout>
    </>
  )
}
